import { FC, useState } from 'react';
import { Button, Col, Form, Input, Row, Select, Space, Table } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import { useModals } from '@/components/modals/ModalProvider';
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/App';
import { getCreditCheckList } from '@/libs/fleet-api/services/prospect';
import { FleetPagination, FleetQueryParams } from '@/libs/fleet-api/fetcher';
import GenericErrorMessage from '@/components/GenericErrorMessage';

const CCAAnalisaSLIKPage: FC = () => {
	const { navigate } = useApp();
	const { openCustomerDetail } = useModals();

	const [searchBy, setSearchBy] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState<string | null>(null);

	const [pagination, setPagination] = useState<FleetPagination>({
		pageNumber: 1,
		pageSize: 20,
	});
	const [queryParams, setQueryParams] = useState<FleetQueryParams>({
		search: undefined,
		filter: undefined,
		pagination,
	});

	const handleSearch = () => {
		setQueryParams((current) => {
			if (!searchBy || !searchValue) {
				return {
					...current,
					search: undefined,
				};
			} else if (searchBy === 'status') {
				return {
					...current,
					search: undefined,
					reviewStatus: parseInt(searchValue),
				};
			}
			return {
				...current,
				search: {
					by: searchBy,
					value: searchValue,
				},
			};
		});
	};

	const { data, isLoading, isError } = useQuery({
		queryKey: ['getCreditCheckList', queryParams] as const,
		queryFn: ({ queryKey }) => {
			const [, queryParams] = queryKey;
			return getCreditCheckList(queryParams);
		},
	});

	const dataSource = data?.data.items ?? [];

	if (isError) return <GenericErrorMessage />;

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<FleetTitle>Analisa SLIK</FleetTitle>
			<Form
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={handleSearch}
			>
				<Row gutter={[20, 20]}>
					<Col flex="auto">
						<Form.Item
							label={<span>Search By</span>}
							labelCol={{ flex: 'auto' }}
							wrapperCol={{ flex: 'none', style: { width: 250 } }}
						>
							<Select
								placeholder="Search By"
								options={[
									{
										label: 'Customer Name',
										value: 'customer-name',
									},
									{
										label: 'NPWP',
										value: 'npwp',
									},
									{
										label: 'Status',
										value: 'status',
									},
								]}
								style={{
									display: 'block',
									width: '100%',
								}}
								onChange={(value) => {
									setSearchValue(null);
									setSearchBy(value);
								}}
							/>
						</Form.Item>
					</Col>
					<Col style={{ width: 300 }}>
						<Form.Item>
							{searchBy === 'status' ? (
								<Select
									options={[
										{ label: 'New', value: '1' },
										{ label: 'Return', value: '2' },
										{ label: 'Reject', value: '3' },
									]}
									value={searchValue}
									onChange={(value) => {
										setSearchValue(value);
									}}
									placeholder="Select Status"
								/>
							) : (
								<Input
									placeholder="Search..."
									style={{
										display: 'block',
									}}
									disabled={searchBy === null}
									value={searchValue!}
									onChange={(e) => {
										setSearchValue(e.target.value);
									}}
								/>
							)}
						</Form.Item>
					</Col>
					<Col>
						<Form.Item>
							<Button
								block
								type="primary"
								htmlType="submit"
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Table
				scroll={{ x: 'max-content' }}
				loading={isLoading}
				bordered
				dataSource={dataSource.map((data, key) => {
					return { ...data, key };
				})}
				pagination={{
					total: data?.data.totalCount,
					current: pagination.pageNumber,
					pageSize: pagination.pageSize,
					onChange: (pageNumber, pageSize) => {
						setPagination((current) => {
							return {
								pageSize,
								pageNumber: current.pageSize !== pageSize ? 1 : pageNumber,
							};
						});
						setQueryParams((current) => {
							return {
								...current,
								pagination: {
									pageSize,
									pageNumber:
										current.pagination?.pageSize !== pageSize ? 1 : pageNumber,
								},
							};
						});
					},
				}}
				columns={[
					{
						title: 'No',
						width: 1,
						render: (_, { no }) => no,
					},
					{
						title: 'Prospect ID',
						render: (_, { prospectId }) => prospectId,
					},
					{
						title: 'Customer Name',
						render: (_, { customerId, customerName }) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(customerId);
								}}
							>
								{customerName}
							</a>
						),
					},
					{
						title: 'Group Customer BMPP',
						render: (_, { customerGroupName }) => customerGroupName ?? '-',
					},
					{
						title: 'Department',
						render: (_, { department }) => department,
					},
					{
						title: 'Fleet RM',
						render: (_, { frmName }) => frmName,
					},
					{
						title: 'New Order Date',
						render: (_, { prospectCreatedAt }) => prospectCreatedAt,
					},
					{
						title: 'Status',
						align: 'center',
						render: (_, record) => {
							return (
								<span
									className={`review-status ${record.reviewStatus.toLowerCase()}`}
								>
									{record.reviewStatus.toLowerCase()}
								</span>
							);
						},
					},
					{
						title: 'Select',
						align: 'center',
						render: (_, { prospectId, status, reviewStatus }) => {
							if (status !== 'CCH' && reviewStatus !== 'RETURN') {
								return <ArrowRightOutlined style={{ color: '#bababa' }} />;
							}
							return (
								<a
									onClick={(e) => {
										e.preventDefault();
										navigate('CCAAnalisaSLIKForm', { prospectId });
									}}
								>
									<ArrowRightOutlined />
								</a>
							);
						},
					},
				]}
			/>
		</Space>
	);
};

export default CCAAnalisaSLIKPage;
